/**
 * enums
 */
import { IUploadItemStatus, UploadItemStatus } from '../enums';
import { ThemeColorsVariants } from '@core/enums/theme-colors-variants';

/**
 * models
 */
import { BadgeModel, BadgeAppearance } from '@sharedDirectives/badge';

/**
 * consts
 */
import { UploadTranslateMapper } from './upload-translate-mapper';

/**
 * Builds the badge of one file inside a batch: a processed file shows
 * green as "Completed", a failed one red, and a pending one amber as
 * "Processing".
 *
 * @param raw - The status word sent by the server, or null when it is
 * missing or not one of the three known words. Anything else shows the
 * grey "unknown" badge rather than breaking the row.
 * @returns The badge to render.
 */
export function createUploadItemStatusBadge(raw: IUploadItemStatus | null | undefined): BadgeModel<IUploadItemStatus | null> {

    const value = Object.values(UploadItemStatus).includes(raw as UploadItemStatus) ? raw as UploadItemStatus : null;

    if (value == null) {
        return new BadgeModel<IUploadItemStatus | null>(null, UploadTranslateMapper.STATUS.UNKNOWN(), ThemeColorsVariants.Secondary, BadgeAppearance.Label);
    }

    const labels: Record<UploadItemStatus, string> = {
        [UploadItemStatus.Processed]: UploadTranslateMapper.STATUS.COMPLETED(),
        [UploadItemStatus.Failed]: UploadTranslateMapper.STATUS.FAILED(),
        [UploadItemStatus.Pending]: UploadTranslateMapper.STATUS.PROCESSING(),
    };

    const variants: Record<UploadItemStatus, ThemeColorsVariants> = {
        [UploadItemStatus.Processed]: ThemeColorsVariants.Success,
        [UploadItemStatus.Failed]: ThemeColorsVariants.Danger,
        [UploadItemStatus.Pending]: ThemeColorsVariants.Warning,
    };

    return new BadgeModel<IUploadItemStatus | null>(value, labels[value], variants[value], BadgeAppearance.Label);
}

/**
 * Builds the badge of a whole batch.
 *
 * A batch only reports whether the server is done with it, not how each
 * file went, so it is either green "Completed" or amber "Processing" —
 * the per-file outcomes are read from the expanded rows.
 *
 * @param processingCompleted - Whether the server finished the batch.
 * @returns The badge to render.
 */
export function createUploadBatchStatusBadge(processingCompleted: boolean): BadgeModel<boolean> {

    return processingCompleted
        ? new BadgeModel<boolean>(true, UploadTranslateMapper.STATUS.COMPLETED(), ThemeColorsVariants.Success, BadgeAppearance.Label)
        : new BadgeModel<boolean>(false, UploadTranslateMapper.STATUS.PROCESSING(), ThemeColorsVariants.Warning, BadgeAppearance.Label);
}
